// Keeps a page from staying on an old version. Nobody presses an "update" button: the page asks the server which build is current, lets the new
// service worker take over, and reloads itself at a moment when that costs the guest nothing. The queue is in IndexedDB, so a reload loses nothing
// that was already queued.
//
// "A moment that costs nothing" is the caller's word (`isSafe`): for the guest page it is the start screens and the done screen with an empty queue,
// never while photos are being picked or sent; for the live wall it is the QR takeover, never mid-photo. On top of that, on every page: never while
// someone is typing in a field (Tom, 27 Oct 2026); the reload waits until the field is left, or the next time the page opens.
//
// And never in the middle of the swap of the service worker: the reload waits until the new worker has finished taking over (bounded). A reload while
// the new worker's "activate" deleted the old caches left the guest a blank page (27 Oct 2026; the worker now also never fails on a cache).

export interface UpdaterOptions {
  /** The build this page is. */
  build: string
  /** True at a moment when reloading costs the guest nothing. Asked again whenever something might have changed. */
  isSafe: () => boolean
  /** How often to ask the server. 0 turns the timer off (the page still asks on load and when it comes back into view). */
  intervalMs?: number
  /** The build the server says is current, or null when it cannot be learned (offline, a login page). */
  fetchBuild?: () => Promise<string | null>
  reload?: () => void
  /** Asks the browser to look for a new service worker. */
  updateWorker?: () => Promise<void>
  /** Resolves when no new service worker is installing, waiting or activating any more. */
  /** Tells a new worker that has installed that it may take over now (it waits until told). Called only right before the page's own reload. */
  activateWorker?: () => Promise<void>
  workerSettled?: () => Promise<void>
  /** True while someone is typing in a field of the page. */
  isTyping?: () => boolean
  /** Survives a reload, so that one remote build is only ever reloaded for once. */
  storage?: Pick<Storage, 'getItem' | 'setItem'>
}

/** How long a reload waits for the browser to fetch the new service worker and let it take over. The page itself is fetched fresh either way (HTML is
 *  network-first). Generous, because a cheap phone installs slowly; bounded, because a stuck worker must not hold the reload back for ever. */
export const WORKER_UPDATE_WAIT_MS = 15_000

const RELOADED_FOR = 'kwento:reloadedFor'
const FORCED = 'kwento:forcedUpdates'

export class Updater {
  /** The newer build we know about and have not yet reloaded for. Shown on the page as `data-kwento-update`, so a test (and a support person) can see it. */
  get pending(): string | null {
    return this._pending
  }

  set pending(build: string | null) {
    this._pending = build
    try {
      if (build) document.documentElement.dataset.kwentoUpdate = build
      else delete document.documentElement.dataset.kwentoUpdate
    } catch {
      // no document (a unit test, a worker): the marker is a courtesy
    }
  }

  private _pending: string | null = null
  private timer: ReturnType<typeof setInterval> | null = null
  private readonly o: Required<Omit<UpdaterOptions, 'storage'>> & { storage: Pick<Storage, 'getItem' | 'setItem'> | null }

  constructor(options: UpdaterOptions) {
    this.o = {
      intervalMs: 15 * 60_000,
      fetchBuild: defaultFetchBuild,
      reload: () => location.reload(),
      updateWorker: defaultUpdateWorker,
      activateWorker: defaultActivateWorker,
      workerSettled: defaultWorkerSettled,
      isTyping: defaultIsTyping,
      storage: (() => {
        try {
          return sessionStorage
        } catch {
          return null
        }
      })(),
      ...options,
    }
  }

  start(): () => void {
    void this.check()
    const onVisible = () => {
      if (document.visibilityState === 'visible') void this.check()
    }
    document.addEventListener('visibilitychange', onVisible)
    // Leaving a field may be the moment a held-back reload was waiting for. Asked after the focus has moved, so the next field (if any) counts.
    const onFocusOut = () => setTimeout(() => this.maybeReload(), 0)
    document.addEventListener('focusout', onFocusOut)
    // A new worker that takes control of a page that WAS controlled means a new build is out, whether or not version.json has been read yet. On a
    // first visit there is no controller yet, and the worker claiming the page is installation, not an update: reloading then would throw away the
    // first seconds of every new guest's visit (and cancel whatever the page had just started).
    const wasControlled = Boolean(navigator.serviceWorker?.controller)
    const onControllerChange = () => {
      if (!wasControlled) return
      void this.check({ workerChanged: true })
    }
    navigator.serviceWorker?.addEventListener('controllerchange', onControllerChange)
    if (this.o.intervalMs > 0) this.timer = setInterval(() => void this.check(), this.o.intervalMs)
    return () => {
      document.removeEventListener('visibilitychange', onVisible)
      document.removeEventListener('focusout', onFocusOut)
      navigator.serviceWorker?.removeEventListener('controllerchange', onControllerChange)
      if (this.timer) clearInterval(this.timer)
    }
  }

  /**
   * Asks the server which build is current. If it is not this one, the new worker is fetched and, when it is safe, the page reloads.
   *
   * `workerChanged`: a new worker has just taken over this page. That does not by itself mean the page is old: after a reload for an update the page
   * is already the new build and the worker arrives a moment later, and reloading again would be the second reload for one deploy. So the server is asked;
   * only when it cannot be asked (offline, a login page) is the takeover taken as the sign that a newer build is out.
   */
  async check(opts: { workerChanged?: boolean } = {}): Promise<void> {
    let remote: string | null = null
    try {
      remote = await this.o.fetchBuild()
    } catch {
      // offline or a login page: nothing is learned
    }
    if (remote && remote !== this.o.build && remote !== this.reloadedFor()) {
      this.pending = remote
      // The new worker is fetched and installed now; it waits, and takes over only right before the page reloads (swapThenReload). Bounded.
      await Promise.race([this.o.updateWorker().catch(() => {}), new Promise<void>((r) => setTimeout(r, WORKER_UPDATE_WAIT_MS))])
    } else if (remote === null && opts.workerChanged) {
      this.pending ??= 'worker'
    }
    await this.maybeReload()
  }

  /** Called whenever the answer to `isSafe` may have changed (the screen changed, the tab came back). */
  maybeReload(): Promise<void> {
    if (!this.pending || !this.o.isSafe() || this.o.isTyping()) return Promise.resolve()
    this.o.storage?.setItem(RELOADED_FOR, this.pending)
    this.pending = null
    return this.swapThenReload()
  }

  /**
   * The new worker takes over only NOW, when the page itself is about to reload (30 Oct 2026). A worker that took over by itself, while the guest had
   * just reloaded the page, killed every request of that page (net::ERR_FAILED) and left it blank; a waiting worker cannot do that. The reload waits
   * until the swap is done, so the reloaded page never meets it half-way. Bounded: a slow or stuck worker must not hold the reload back for ever.
   */
  private async swapThenReload(): Promise<void> {
    const swap = (async () => {
      await this.o.activateWorker().catch(() => {})
      await this.o.workerSettled().catch(() => {})
    })()
    await Promise.race([swap, new Promise<void>((r) => setTimeout(r, WORKER_UPDATE_WAIT_MS))])
    this.o.reload()
  }

  /**
   * The server said this page is too old to talk to it. Reload NOW, whatever the page is doing: the queue is on the device. Escalates once if the
   * reload brought back the same old page (a stale cached copy): the second time every cache and the worker are removed first. After that it gives
   * up rather than loop.
   */
  async forceUpdate(): Promise<void> {
    const n = Number(this.o.storage?.getItem(FORCED) ?? '0')
    if (n >= 2) return
    this.o.storage?.setItem(FORCED, String(n + 1))
    if (n >= 1) await clearEverything()
    else await this.o.updateWorker().catch(() => {})
    this.o.reload()
  }

  private reloadedFor(): string | null {
    return this.o.storage?.getItem(RELOADED_FOR) ?? null
  }
}

async function defaultFetchBuild(): Promise<string | null> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), 5000)
  try {
    const res = await fetch('/version.json', { cache: 'no-store', signal: controller.signal })
    if (!res.ok || !/json/i.test(res.headers.get('content-type') ?? '')) return null // a login page is not an answer
    const body = (await res.json()) as { build?: unknown }
    return typeof body.build === 'string' ? body.build : null
  } finally {
    clearTimeout(timer)
  }
}

async function defaultUpdateWorker(): Promise<void> {
  const reg = await navigator.serviceWorker?.getRegistration()
  await reg?.update()
}

/** Tells the new worker, once it has installed, that it may take over (it waits until told: public/sw.js, "skip-waiting"). */
async function defaultActivateWorker(): Promise<void> {
  const reg = await navigator.serviceWorker?.getRegistration()
  const worker = reg?.installing ?? reg?.waiting
  if (!worker) return
  if (worker.state === 'installing') {
    await new Promise<void>((resolve) => {
      const done = () => {
        if (worker.state === 'installing') return
        worker.removeEventListener('statechange', done)
        resolve()
      }
      worker.addEventListener('statechange', done)
      done()
    })
  }
  if (worker.state === 'installed') worker.postMessage({ type: 'skip-waiting' })
}

/** Waits while a new worker is installing, waiting or activating; returns at once when there is none. */
async function defaultWorkerSettled(): Promise<void> {
  const reg = await navigator.serviceWorker?.getRegistration()
  const worker = reg?.installing ?? reg?.waiting ?? (reg?.active?.state === 'activating' ? reg.active : null)
  if (!worker) return
  await new Promise<void>((resolve) => {
    const done = () => {
      if (worker.state !== 'activated' && worker.state !== 'redundant') return
      worker.removeEventListener('statechange', done)
      resolve()
    }
    worker.addEventListener('statechange', done)
    done()
  })
}

/** A field someone can type in has the focus. Buttons, boxes and file pickers do not count. */
export function defaultIsTyping(): boolean {
  try {
    const el = document.activeElement as HTMLElement | null
    if (!el) return false
    if (el.isContentEditable || el.tagName === 'TEXTAREA') return true
    if (el.tagName !== 'INPUT') return false
    return !['button', 'submit', 'reset', 'checkbox', 'radio', 'file', 'range', 'color', 'image', 'hidden'].includes((el as HTMLInputElement).type)
  } catch {
    return false // no document (a unit test, a worker)
  }
}

async function clearEverything(): Promise<void> {
  try {
    for (const key of await caches.keys()) await caches.delete(key)
    for (const reg of (await navigator.serviceWorker?.getRegistrations()) ?? []) await reg.unregister()
  } catch {
    // best effort
  }
}
